import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import BREAKPOINTS from '@mlb/react-patterns/style/breakpoints.ts';
import SkeletonPattern from '@mlb/react-patterns/components/Skeleton/skeleton.tsx';
import { media } from '@mlb/react-patterns/style/helpers/media.ts';

import { useImageLoaded } from 'bdvp-common/src/hooks';

import getSrcset from '../../utils/getSrcset';

const formatInstructions = 'ar_{ratio},g_auto,q_auto:good,w_{width},c_fill,f_jpg';

const formatInstructionSet = {
    xl: {
        ratio: '4:1',
        widths: [
            1024,
            1536,
            1920,
            2208,
            2608,
        ],
    },
    sm: {
        ratio: '7:2',
        widths: [
            372,
            640,
            750,
            1024,
            1536,
            1920,
            2208,
            2608,
        ],
    },
    default: {
        ratio: '16:9',
        widths: [
            372,
            640,
            750,
            1024,
            1536,
            1920,
        ],
    },
};

const getRatioPadding = (width, height) => `${(height/width) * 100}%`;

const Skeleton = styled(SkeletonPattern)`
    overflow: hidden;
    padding-bottom: ${getRatioPadding(16,9)};

    ${media.smmin`
        padding-bottom: ${getRatioPadding(7,2)};
    `}

    ${media.xlmin`
        padding-bottom: ${getRatioPadding(4,1)};
    `}
`;

const Picture = styled.picture`
    opacity: 0.6;
`;

const Image = (props) => {

    const {
        alt,
        loading,
        templateUrl,
    } = props;

    const [
        imgRef,
        imgLoaded,
        setImgLoaded,
    ] = useImageLoaded();

    const {
        xlSrcSet,
        smSrcSet,
        defaultSrcSet,
    } = useMemo(() => {

        const template = templateUrl.replace('{formatInstructions}', formatInstructions);

        return {
            xlSrcSet: getSrcset(template, formatInstructionSet.xl),
            smSrcSet: getSrcset(template, formatInstructionSet.sm),
            defaultSrcSet: getSrcset(template, formatInstructionSet.default),
        };

    }, [templateUrl]);

    return (
        <Skeleton
            height={'0'}
            margin={'0'}
            borderRadius={'0'}
            isLoading={!imgLoaded}
        >
            {!loading && (
                <Picture>
                    <source
                        media={`(min-width: ${BREAKPOINTS.xl})`}
                        srcSet={xlSrcSet}
                    />
                    <source
                        media={`(min-width: ${BREAKPOINTS.sm})`}
                        srcSet={smSrcSet}
                    />
                    <img
                        ref={imgRef}
                        onLoad={setImgLoaded}
                        onError={setImgLoaded}
                        srcSet={defaultSrcSet}
                        alt={alt}
                        width={'100%'}
                    />
                </Picture>
            )}
        </Skeleton>
    );

};

Image.propTypes = {
    loading: PropTypes.bool.isRequired,
    alt: PropTypes.string,
    templateUrl: PropTypes.string,
};

Image.defaultProps = {
    alt: '',
    templateUrl: '',
};

export default Image;
